import React, { useState, useEffect } from 'react';
import QrReader from 'react-qr-scanner';
import { useStyletron } from 'baseui';

const ScannerPage = () => {
    const [css] = useStyletron();
    const [qrData, setQrData] = useState('');
    const [message, setMessage] = useState('');
    const [customer, setCustomer] = useState(null);
    const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 500);
    // A key for QrReader so we can force it to remount when needed.
    const [scannerKey, setScannerKey] = useState(0);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 500);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Continuous scanning: extract text property if available.
    const handleScan = (data) => {
        if (data) {
            // Clear previous customer details when a new code is scanned.
            setCustomer(null);
            if (typeof data === 'object' && data.text) {
                setQrData(data.text);
            } else if (typeof data === 'object') {
                setQrData(JSON.stringify(data));
            } else {
                setQrData(data);
            }
        }
    };

    const handleError = (err) => {
        console.error(err);
        setMessage('Error scanning QR code.');
        clearMessageAfterTimeout();
    };

    // Updated clearMessageAfterTimeout: clear both message and customer.
    const clearMessageAfterTimeout = () => {
        setTimeout(() => {
            setMessage('');
            setCustomer(null);
        }, 15000); // Clear both message and customer after 15 seconds
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/scan`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ barcode: qrData }),
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Scan failed');
            }
            const result = await response.json();
            setMessage(result.message || 'Scan recorded successfully!');
            // If backend returns customer data, store it.
            if (result.customer) {
                setCustomer(result.customer);
            } else {
                setCustomer(null);
            }
            clearMessageAfterTimeout();
            setQrData('');
            // Remount the QR scanner by updating the key.
            setScannerKey((prev) => prev + 1);
        } catch (error) {
            setMessage(`Error: ${error.message}`);
            setCustomer(null);
            clearMessageAfterTimeout();
        }
    };

    // Clear function: resets qrData, customer, and message, and forces the scanner to remount.
    const handleClear = () => {
        setQrData('');
        setCustomer(null);
        setMessage('');
        setScannerKey((prev) => prev + 1);
    };

    // Determine message color: green for success, red for errors.
    const messageStyle = { color: message.startsWith("Error:") ? 'red' : 'green' };

    // Outer container applies the background to the whole page.
    const outerContainerStyle = css({
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #d23c1b 0%, #BA5A31 48.55%, #E59F71 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
    });

    // Inner container for the card.
    const containerStyle = css({
        width: '100%',
        maxWidth: '420px',
        padding: '1.5rem',
        background: '#fff',
        borderRadius: '8px',
        textAlign: 'center',
        color: '#333',
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    });

    const headerStyle = css({
        fontFamily: 'Impact, sans-serif',
        fontSize: '2.5rem',
        marginBottom: '1rem',
    });

    const previewStyle = {
        height: 240,
        width: 320,
        margin: '0 auto',
    };

    // Styles for input and buttons.
    const inputStyle = {
        width: 'calc(100% - 20px)',
        padding: '0.75rem',
        fontSize: '1rem',
        borderRadius: '4px',
        border: '1px solid #ccc',
        marginBottom: '1rem',
    };

    const buttonStyle = {
        padding: '0.75rem 1.5rem',
        fontSize: '1rem',
        borderRadius: '4px',
        border: 'none',
        backgroundColor: '#d23c1b',
        color: '#fff',
        cursor: 'pointer',
        marginRight: '0.5rem',
    };

    const clearButtonStyle = {
        ...buttonStyle,
        backgroundColor: '#BA5A31',
    };

    return (
        <div className={outerContainerStyle}>
            <div className={containerStyle}>
                <h1 className={headerStyle}>Scan Your Ticket</h1>
                {message && <p style={messageStyle}>{message}</p>}
                {isMobile ? (
                    <div style={{ marginBottom: '1rem' }}>
                        <QrReader
                            key={scannerKey} // Force remount when scannerKey changes.
                            delay={300}
                            onError={handleError}
                            onScan={handleScan}
                            style={previewStyle}
                            constraints={{ video: { facingMode: "environment" } }}

                        />
                    </div>
                ) : (
                    <p>QR scanning is available on mobile devices only.</p>
                )}
                {qrData && (
                    <div style={{ marginTop: '1rem' }}>
                        <h3>Scanned Data:</h3>
                        <p>{qrData}</p>
                    </div>
                )}
                {customer && (
                    <div style={{ marginTop: '1rem', color: 'green' }}>
                        <h3>Customer Info:</h3>
                        <p>Name: {customer.name}</p>
                        <p>Email: {customer.email}</p>
                    </div>
                )}
                {/* Manual input and submit section */}
                <form onSubmit={handleSubmit}>
                    <input
                        type="text"
                        value={qrData}
                        onChange={(e) => setQrData(e.target.value)}
                        placeholder="Enter or scan barcode"
                        style={inputStyle}
                    />
                    <div>
                        <button type="submit" style={buttonStyle}>
                            Submit Scan
                        </button>
                        <button type="button" style={clearButtonStyle} onClick={handleClear}>
                            Clear
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ScannerPage;
